import React, { useState, useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { useNotification } from 'config/Notification';

import { fetchCompaniesService } from 'services/modules/idp/company';
import { getAgents as getAgentsService, getUpdatedTime as getUpdatedTimeService, downloadAlertsByAgent as downloadAlertsByAgentService } from 'services/modules/soc/agent';

import { Form, Modal, Button, FloatingLabel } from 'react-bootstrap';

import * as XLSX from "xlsx";
import Select from "react-select";

import {
    CircularProgress,
} from '@mui/material';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';

import { DatePicker, Space } from "antd";
import dayjs from "dayjs";

import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

const { RangePicker } = DatePicker;

dayjs.extend(timezone);
dayjs.extend(utc);


const DownloadSOCReport = ({ toggleReportDownload }) => {

    // State Variables
    const [loading, setLoading] = useState(false);
    const [selectedCompany, setSelectedCompany] = useState("");
    const [selectedAgent, setSelectedAgent] = useState("")
    const [show, setShow] = useState(true);
    const [companies, setCompanies] = useState([]);
    const [agents, setAgents] = useState([]);
    const [alerts, setAlerts] = useState([]);
    const [updatedTime, setUpdatedTime] = useState(null);
    const [startDateTime, setStartDateTime] = useState(null);
    const [endDateTime, setEndDateTime] = useState(null);
    const [maxValue, setMaxValue] = useState(null);

    // Redux selectors
    const companiesContainer = useSelector((state) => state.company.companies);

    // Redux dispatch
    const dispatch = useDispatch();

    // Notification
    const { openNotification } = useNotification();

    useEffect(() => {
        fetchSOCCompanies();
        const start = new Date(new Date().getTime() - 24 * 60 * 60 * 1000);
        setStartDateTime(start);
        setEndDateTime(new Date())
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (selectedAgent) {
            fetchUpdateTime();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAgent])

    useEffect(() => {
        if (selectedCompany) {
            setAgents([])
            fetchAgents();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCompany])

    useEffect(() => {
        if (selectedAgent) {
            fetchAlertsByAgentId();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAgent, startDateTime, endDateTime])




    // MEthod handles to fetch soc companies
    const fetchSOCCompanies = async () => {
        try {
            const params = { "sortBy": "-createdAt", };
            params['x-app-slug'] = "soc"
            dispatch(fetchCompaniesService({ params }));
            if (companiesContainer) {
                await dispatch(fetchCompaniesService('soc'))
                const companies = companiesContainer?.results?.filter((company) => company.applications.some(app => app.slug === 'soc'));
                const socCompanies = companies.map((company) => ({
                    company_name: company.name,
                    company_id: company.id,
                    soc_env: company.soc_env,
                    soc_group: company.soc_group
                }))
                setCompanies(socCompanies)
            }
        } catch (error) {
            console.error(error)
        }
    }

    // Method handles to fetch Agents based on selected company
    const fetchAgents = async () => {
        try {
            setLoading(true);
            const socgroup = companies.filter((company) => company.company_id === selectedCompany?.value)[0]?.soc_group;
            const socenv = companies.filter((company) => company.company_id === selectedCompany?.value)[0]?.soc_env;
            const params = {
                group: socgroup,
                soc_env: socenv
            }
            if (socgroup && socenv) {
                const response = await getAgentsService(params);
                if (response?.data?.affected_items) {
                    setAgents(response?.data?.affected_items);
                }
            }
        } catch (error) {
            console.error(error, "error")
        } finally {
            setLoading(false);
        }

    }

    // Method handles alerts based on agentid and timerange
    const fetchAlertsByAgentId = async () => {
        try {
            const socenv = companies.filter((company) => company.company_id === selectedCompany.value)[0]?.soc_env;
            if (selectedAgent && socenv && startDateTime && endDateTime) {
                setAlerts([]);
                const params = {
                    severity: "high,critical",
                    soc_env: socenv,
                    start: startDateTime,
                    end: endDateTime,
                    agentId: selectedAgent
                };
                const response = await downloadAlertsByAgentService(params);
                if (response && response?.data && socenv) {
                    const groupedData = response.data.map((hit) => hit);
                    setAlerts(groupedData ?? []);
                }
            }
        } catch (error) {
            console.error(error);
        }
    };

    // Method to fetch the agent latest update time
    const fetchUpdateTime = async () => {
        const socenv = companies.filter((company) => company.company_id === selectedCompany.value)[0]?.soc_env;
        const params = { agentId: selectedAgent, soc_env: socenv };
        const response = await getUpdatedTimeService(params);
        if (response?.data?.[0]) {
            setUpdatedTime(response?.data?.[0]?.modified_date);
        } else {
            setUpdatedTime(null);
        }
    }

    // Method handles to flatten nested objects
    const flattenObject = (obj, parentKey = "", result = {}) => {
        for (let key in obj) {
            if (obj.hasOwnProperty(key)) {
                const newKey = parentKey ? `${parentKey}.${key}` : key;
                const value = obj[key];
                if (typeof value === "object" && value !== null) {
                    if (Array.isArray(value)) {
                        result[newKey] = value.join(", ");
                    } else {
                        flattenObject(value, newKey, result);
                    }
                } else {
                    result[newKey] = value;
                }
            }
        }
        return result;
    };

    // Method handles to downlaod excel report
    const DownloadExcelReport = () => {
        try {
            if (selectedAgent) {
                if (alerts.length > 0) {
                    const flattenedData = alerts.map(entry => flattenObject(entry._source));
                    const worksheet = XLSX.utils.json_to_sheet(flattenedData);
                    const workbook = XLSX.utils.book_new();
                    XLSX.utils.book_append_sheet(workbook, worksheet, "Alerts");
                    XLSX.writeFile(workbook, "alerts_report.xlsx");
                } else {
                    openNotification("No alerts found in given timerange", "error")
                }
            } else {
                openNotification("Please select agent", "warning")
            }
        } catch (error) {
            console.error(error)
        }
    }


    // Handles to close the popup
    const closing = () => {
        setShow(false);
        toggleReportDownload();
    }

    useEffect(() => {
        let start, end;

        if (updatedTime) {
            end = convertToIST(new Date(updatedTime).toISOString());
            const parsedDate = new Date(updatedTime);

            if (isNaN(parsedDate.getTime())) {
                console.error("Invalid Date:", updatedTime);
            }
            setMaxValue(parsedDate);
        } else {
            end = new Date();
            end.setHours(0, 0, 0, 0);
        }

        start = convertToIST(new Date(end.getTime() - 24 * 60 * 60 * 1000).toISOString()); // Set start to 24 hours before end

        setStartDateTime(start);
        setEndDateTime(end);
    }, [updatedTime]);

    const convertToIST = (dateString) => {
        // Check if string includes an explicit offset (e.g., +, -)
        if (dateString.includes('+') || dateString.includes('-')) {
            // Parse as is and convert to IST, returning a Date object
            return dayjs(dateString).tz("Asia/Kolkata", true).toDate();
        } else {
            // Assume it's UTC if it has 'Z', convert to IST and return a Date object
            return dayjs.utc(dateString).tz("Asia/Kolkata", true).toDate();
        }
    };

    // This method handles change in selected company
    const onInputChange = async (event, value, reason) => {

        if (value !== "") {
            let params = {
                page: '1',
                limit: 10,
                search: value,
            };
            params['x-app-slug'] = "soc"
            dispatch(fetchCompaniesService({ params }));
            if (companiesContainer) {
                await dispatch(fetchCompaniesService('soc'))
                const companies = companiesContainer?.results?.filter((company) => company?.applications?.some(app => app.slug === 'soc'));
                const socCompanies = companies?.map((company) => ({
                    company_name: company.name,
                    company_id: company.id,
                    soc_env: company.soc_env,
                    soc_group: company.soc_group
                })) || []
                setCompanies(socCompanies)
            }
        } else {
            fetchSOCCompanies()
        }
    }

    return (
        <Modal show={show} onHide={closing} dialogClassName="modal-90w" animation={false}>
            <Modal.Header className="space-between" closeButton>
                <Modal.Title className="report-modal">
                    <LibraryBooksIcon className="m-1" />
                    <h4 className="report-heading-1"> Download SOC Report </h4>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className='m-3'>
                <>
                    {updatedTime && <span className='ms-3'><strong>Latest Update Time:</strong> {new Date(updatedTime).toLocaleString()} </span>}
                    {loading ?
                        <div
                            className="d-flex justify-content-center align-items-center mt-4git "
                        >
                            <CircularProgress />
                        </div>
                        : (
                            <div>

                                <div className="row m-3">
                                    <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12">
                                        {/* <Form.Group >
                                            <div className="custom-floating-select">
                                                <FloatingLabel controlId="floatingSelect" label="Company">
                                                    <Form.Select value={selectedCompany} onChange={(e) => setSelectedCompany(e.target.value)}>
                                                        <option value="" disabled>
                                                            Choose Company
                                                        </option>
                                                        {companies?.map((option, index) => (
                                                            <option key={index} value={option.company_id}>
                                                                {option.company_name}
                                                            </option>
                                                        ))}
                                                    </Form.Select>
                                                </FloatingLabel>
                                            </div>
                                        </Form.Group> */}

                                        <div className="custom-floating-select">
                                            <FloatingLabel controlId="floatingSelect" label="Company">
                                                <Select
                                                    options={companies?.map(option => ({
                                                        value: option.company_id,
                                                        label: option.company_name
                                                    }))}
                                                    value={selectedCompany}
                                                    // onChange={(e) => setSelectedCompany(e.target.value)}
                                                    onChange={(company) => {
                                                        setSelectedCompany(company || null);
                                                    }}
                                                    onInputChange={(value, { action }) => {
                                                        // if (action === 'input-change' && value) {
                                                        //     onInputChange(null, value, action);
                                                        // }
                                                        onInputChange(null, value, action)
                                                    }}
                                                    isClearable
                                                    placeholder="Choose Company"
                                                    noOptionsMessage={() => "No Company found"}

                                                />
                                            </FloatingLabel>
                                        </div>
                                    </div>
                                    <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12">
                                        <Form.Group>
                                            <div className="custom-floating-select">
                                                <FloatingLabel controlId="floatingSelect" label="Agents">
                                                    <Form.Select value={selectedAgent} onChange={(e) => setSelectedAgent(e.target.value)} isInvalid={selectedCompany && agents.length === 0}
                                                    >
                                                        {selectedCompany && agents.length === 0 ? (
                                                            <option value="" disabled>
                                                               No Agents Found
                                                            </option>
                                                        ) :(
                                                            <option value="" disabled>
                                                            Choose Agents
                                                        </option>
                                                        )
                                                    }
                                                        {agents?.map((option, index) => (
                                                            <option key={index} value={option.id}>
                                                                {option.name}
                                                            </option>
                                                        ))}
                                                    </Form.Select>
                                                </FloatingLabel>
                                                <Form.Control.Feedback type="invalid">
                                                    No agents found for company
                                                </Form.Control.Feedback>
                                            </div>
                                        </Form.Group>
                                    </div>
                                </div>
                                <div className="row m-3" >

                                    <div className="w-full max-w-xl flex flex-row gap-4" >
                                        <Space direction="vertical" size={12} >
                                            <RangePicker
                                                showTime
                                                getPopupContainer={(triggerNode) => triggerNode.parentNode}
                                                value={[dayjs(startDateTime), dayjs(endDateTime)]}
                                                format="YYYY-MM-DD HH:mm:ss.SSS"
                                                onChange={(dates) => {
                                                    if (dates && dates[0] && dates[1]) {
                                                        const start = convertToIST(dates[0].toDate().toISOString());
                                                        const end = convertToIST(dates[1].toDate().toISOString());

                                                        // Custom range validation
                                                        const diffInMs = end.getTime() - start.getTime();

                                                        if (diffInMs < 0) {
                                                            openNotification(
                                                                "Start date must be before end date",
                                                                "warning"
                                                            );
                                                            return;
                                                        }
                                                        setStartDateTime(start);
                                                        setEndDateTime(end);
                                                    }
                                                }}
                                                disabledDate={(current) => {
                                                    // Disable future dates beyond maxValue
                                                    if (!maxValue) return false;
                                                    return current && current > dayjs(maxValue.toString());
                                                }}
                                            />
                                        </Space>
                                    </div>


                                </div>

                                <div className="report-modal-buttons mt-3">
                                    <Button variant="outline-primary" className="reqreport-btn" disabled={agents.length === 0} onClick={DownloadExcelReport}>
                                        {agents && (
                                            <>
                                                <LibraryBooksIcon className="m-1" /> Download
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </div>
                        )
                    }
                </>
            </Modal.Body>
        </Modal >
    )
}
export default DownloadSOCReport;
